import { useRouter } from 'next/router'
import Script from 'next/script'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { HideAtOrLarger, ShowAtOrLarger } from '@/components/shared/breakpoint'
import { ListingCardContainer } from '@/components/srp/listing-card/styled'
import {
  ValueEstimateButton,
  ValueEstimateDescription,
  ValueEstimateHeading,
  ValueEstimateImage,
} from '@/components/srp/value-estimate/ValueEstimate.styled'
import {
  AC_URL,
  AUTOCAN_CTA_IDS,
  ICO_UTM_PARAMS,
  ICO_UTM_PARAMS_PAGE_SPECIFIC,
} from '@/constants/ico'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { openAutocanValueEstimateModal } from '@/domain/autocanada/openAutocanValueEstimateModal'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { Flex } from '@/ui/atoms/flex'
import { getIcoScriptURL } from '@/utils/autoCanadaHelper'
import { getUserDevice } from '@/utils/getUserDevice'
import { isServer } from '@/utils/isSSR'
import { sendToLogger } from '@/utils/sendToLogger'

const DEFAULT_VALUE_ESTIMATE_POSITION = 3

export type ValueEstimateProps = {
  index: number
  searchProvinceId?: number
}

export const ValueEstimate = ({ index, searchProvinceId }: ValueEstimateProps) => {
  /**
   * This component explicitly needs to know the device type
   * Always prefer CSS when dealing with UI responsiveness
   */
  const isMobile = getUserDevice(isServer() ? undefined : navigator.userAgent).isPhoneOrTablet

  const { data } = useGetSearchResultsData()
  const { data: userSession } = useSession()
  const { push } = useRouter()
  const { routeLocale } = useLocale()
  const { spacing, colors } = useTheme()
  const { t } = useTranslation(TRANSLATION_KEYS.SRP)

  const [valueEstimatePosition, setValueEstimatePosition] = useState(
    DEFAULT_VALUE_ESTIMATE_POSITION
  )

  const { totalCount = 0 } = data?.pagination || {}

  useEffect(() => {
    if (totalCount === 0) {
      // Don't show the component if there are no listings.
      setValueEstimatePosition(-1)
    } else if (totalCount <= DEFAULT_VALUE_ESTIMATE_POSITION) {
      // Show the component at the end if there are too few listings.
      setValueEstimatePosition(totalCount - 1)
    }
  }, [totalCount])

  const handleClick = useCallback(() => {
    trackEvent({ action: GA_EVENT.ValueEstimateBegin })
    openAutocanValueEstimateModal({
      ctaId: AUTOCAN_CTA_IDS.VALUE_ESTIMATE_SRP,
      isMobile: !!isMobile,
      provinceId: searchProvinceId,
      routeLocale,
      user: userSession?.user,
      utmCampaign: ICO_UTM_PARAMS_PAGE_SPECIFIC.SRP.UTM_CAMPAIGN,
      utmContent: ICO_UTM_PARAMS.UTM_CONTENT_ESTIMATE,
      push,
    })
  }, [userSession?.user, isMobile, routeLocale, searchProvinceId, push])

  // Only render the ValueEstimate component when its index matches the calculated position (default is 3)
  if (index !== valueEstimatePosition) {
    return null
  }

  return (
    <li>
      <Script
        id={AC_URL.ICO_SCRIPT_ID}
        onError={(err) => sendToLogger(err, { fingerprint: ['ValueEstimate'] })}
        src={getIcoScriptURL()}
        strategy="lazyOnload"
      />

      <ListingCardContainer>
        <Flex
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
          gap={spacing.default}
        >
          <ValueEstimateImage alt="" src="/next-assets/images/autocanada/ico-srp-cta.png" />
          <Flex flexDirection="column" alignItems="flex-start" gap={spacing.default}>
            <Flex flexDirection="column" alignItems="flex-start" gap={spacing.mini}>
              <ValueEstimateHeading weight="medium">
                {t('value_estimate.heading')}
              </ValueEstimateHeading>
              <ShowAtOrLarger breakpoint="medium">
                <ValueEstimateDescription color={colors.grey.light1}>
                  {t('value_estimate.desktop_description')}
                </ValueEstimateDescription>
              </ShowAtOrLarger>
              <HideAtOrLarger breakpoint="medium">
                <ValueEstimateDescription color={colors.grey.light1}>
                  {t('value_estimate.mobile_description')}
                </ValueEstimateDescription>
              </HideAtOrLarger>
            </Flex>
            <ValueEstimateButton onClick={handleClick}>
              <ShowAtOrLarger breakpoint="medium">
                {t('value_estimate.desktop_button_text')}
              </ShowAtOrLarger>
              <HideAtOrLarger breakpoint="medium">
                {t('value_estimate.mobile_button_text')}
              </HideAtOrLarger>
            </ValueEstimateButton>
          </Flex>
        </Flex>
      </ListingCardContainer>
    </li>
  )
}
